/**
 * levelgen/validate.ts — The specification of a valid level, as executable rules.
 *
 * This is the file to read if you want to know what a level is *allowed* to be.
 * Every rule the authored town obeys — most of them written down in
 * `pavementClearance.test.ts` and `treeClearance.test.ts` after somebody broke
 * them — is restated here so it applies to generated towns too.
 *
 * ## Why the validator doesn't trust the generator
 *
 * The placement code in `town.ts` and `landmarks.ts` already tries to obey
 * these rules; this file checks them again from scratch. That duplication is
 * the point. A generator is a pile of heuristics, and a heuristic that stops
 * working fails *silently* — the level still generates, it's just wrong. With
 * an independent check in front of it the failure mode becomes "that seed was
 * rejected", which is loud, cheap, and can't ship.
 *
 * ## Errors and warnings
 *
 * An **error** means the level must not be played: something is unreachable,
 * something solid is standing where the player has to walk, or the difficulty
 * label is a lie. A **warning** means it will play fine but looks or feels
 * worse than it should — a town with no brick houses in it, a critter that
 * starts in the walker's lap.
 */
import {
  BUILDINGS,
  CAMERA,
  DOG,
  HAZARD,
  LANES,
  LEASH,
  OBJECTIVE,
  PAVEMENT,
  PLAYER,
  ROADS,
  TREES,
} from '../../config/constants';
import { footwayBands } from '../pavement';
import {
  carriagewayRects,
  carriagewayWidth,
  isStreet,
  roadSpan,
  spanReaches,
  spansMap,
} from '../roads';
import { bayMinLength, splitFrontage } from '../terrace';
import { apparentCanopyRect, canopyRect } from '../trees';
import {
  faceGaps,
  frontFacing,
  inflateRect,
  patioRect,
  pointInRect,
  pointRect,
  rectContains,
  rectGap,
  rectsOverlap,
  type Rect,
} from '../level';
import { GRASS_MARGIN } from '../grass';
import { homeDoorstep, homeHouseRect } from '../home';
import { rateLevel } from './difficulty';
import { minRoadGapMs } from './spawns';
import {
  binGreenClearance,
  greenSeparation,
  treeEnvelope,
  treeTrunkTop,
} from './landmarks';
import { buildWalkGrid, floodFrom, isOpen, reachedInside, reachedNear } from './reachability';
import { corridorFor } from './town';
import type { LevelIssue, LevelSpec, Point } from './types';

/**
 * How far a front wall may stand off the pavement before the door stops
 * opening onto it.
 *
 * Nearly nothing, and deliberately so — the whole point of the layout is that
 * a house is built flush to the street. The slack is here for the fractional
 * block sizes a generated town produces, not to license a front garden.
 */
const DOORSTEP_SLACK = 2;

/** Daylight a canopy leaves around a footway. Matches the placer's own margin. */
const FOOTWAY_MARGIN = 8;
/** Nothing interesting within this of anything else interesting. */
const LANDMARK_SEPARATION = 170;
/**
 * How far a bin may stand from the pavement it serves before it stops being
 * street furniture and starts being an object abandoned in a back garden.
 *
 * Sized off the placer's own setback (`landmarks.ts` → `BIN_SETBACK`, the bin's
 * half-width plus a pace of paving) with room for the fractional block edges a
 * generated town produces.
 *
 * There was a second, looser figure beside this one — `BIN_PARK_REACH` — which
 * let a bin justify itself by standing on the hardstanding round a green
 * instead. That is now the one place a bin may *not* be; see
 * `bin-beside-the-grass` below.
 */
const BIN_KERB_REACH = 46;
/** Where the dog spawns relative to the walker (mirrors `Game`'s constructor). */
const DOG_OFFSET = LEASH.REST_LENGTH * 0.8;

/**
 * Check a level from top to bottom.
 *
 * Returns everything wrong with it rather than the first thing, because when a
 * seed fails you want the whole picture — one bad plot usually trips several
 * rules, and fixing the first of them just surfaces the next.
 */
export function validateLevel(spec: LevelSpec): LevelIssue[] {
  const issues: LevelIssue[] = [];
  const error = (code: string, message: string): void => {
    issues.push({ code, severity: 'error', message });
  };
  const warn = (code: string, message: string): void => {
    issues.push({ code, severity: 'warning', message });
  };

  const world: Rect = { x: 0, y: 0, w: spec.world.width, h: spec.world.height };
  const bands = footwayBands(spec.world, spec.roads, ROADS.WIDTH, PAVEMENT.WIDTH);
  const roads = carriagewayRects(spec.world, spec.roads, ROADS.WIDTH);

  checkBounds(spec, error);
  checkStreets(spec, error);
  checkBuildings(spec, bands, world, error, warn);
  checkParks(spec, bands, error);
  checkGrass(spec, error);
  checkTrees(spec, bands, roads, world, error, warn);
  checkLandmarks(spec, bands, world, error);
  checkCompletable(spec, error);
  checkTraffic(spec, error, warn);
  checkHazards(spec, world, error, warn);

  const rating = rateLevel(spec);
  if (rating.difficulty !== spec.difficulty) {
    error(
      'difficulty-mismatch',
      `level is labelled ${spec.difficulty} but rates ${rating.difficulty} ` +
        `(score ${rating.score.toFixed(3)})`,
    );
  }

  return issues;
}

/** Whether a level is fit to play: no errors. Warnings are allowed through. */
export function isPlayable(issues: readonly LevelIssue[]): boolean {
  return !issues.some((i) => i.severity === 'error');
}

type Report = (code: string, message: string) => void;

/** A point, rounded, for a message. There is more than one bin to tell apart. */
const at = (p: Point): string => `(${Math.round(p.x)},${Math.round(p.y)})`;

/**
 * The playable rectangle, and everything that has to be inside it.
 *
 * `bounds` is the ground the walk happens on; the rest of `world` is the
 * surround, which is drawn and never entered (see `levelgen/surround.ts`). The
 * walker is clamped to it, so anything the run needs that falls outside is
 * unreachable by construction — and unreachable in a way the flood fill cannot
 * even report properly, because the fill is *built* from these bounds. That is
 * exactly the kind of rule that has to be checked here rather than left to a
 * later stage to trip over.
 *
 * Checked from scratch rather than trusting the placers, like every other rule
 * in this file: `landmarks.ts` already searches inside the bounds and
 * `spawns.ts` already places critters inside them, and a heuristic that stops
 * working fails silently.
 */
function checkBounds(spec: LevelSpec, error: Report): void {
  const { bounds, world } = spec;
  const map: Rect = { x: 0, y: 0, w: world.width, h: world.height };

  if (bounds.w <= 0 || bounds.h <= 0) {
    error('bounds-empty', 'the level has no ground to walk on');
    return;
  }
  if (!rectContains(map, bounds)) {
    error('bounds-outside-world', 'the ground the walk happens on leaves the map');
    return;
  }

  const inside = (p: Point): boolean => pointInRect(p.x, p.y, bounds);
  if (!inside(spec.start)) {
    error('start-outside-bounds', 'the walker starts outside the ground they can walk on');
  }
  if (spec.bins.length === 0) error('no-bins', 'the level has no bin, so the bag can never go in one');
  for (const bin of spec.bins) {
    if (!inside(bin)) error('bin-outside-bounds', `the bin ${at(bin)} is outside the walk`);
  }
  if (!rectContains(bounds, spec.home)) {
    error('home-outside-bounds', 'the home zone is outside the walk');
  }
  // Grass has been on the critical path since the dog started needing it, so it
  // gets the same treatment as the bin and the door: at least one patch has to
  // be somewhere the dog can actually be taken.
  if (!spec.parks.some((p) => rectsOverlap(p, bounds))) {
    error('grass-outside-bounds', 'every patch of grass is outside the walk');
  }
  for (const spawn of spec.hazards.spawns) {
    // An authored level may leave a critter's position unwritten, which means
    // "scatter it" — and `HazardManager` scatters inside the bounds.
    if (spawn.x === undefined || spawn.y === undefined) continue;
    if (!inside({ x: spawn.x, y: spawn.y })) {
      error(
        'critter-outside-bounds',
        `a ${spawn.type} starts at (${Math.round(spawn.x)},${Math.round(spawn.y)}), ` +
          `outside the ground the walk happens on`,
      );
    }
  }
}

/**
 * The street grid. A road too near the map edge, or two roads too close
 * together, leaves a footway hanging off the map or two of them on top of each
 * other — and the footway is the surface the whole game is played on.
 */
function checkStreets(spec: LevelSpec, error: Report): void {
  if (spec.roads.length === 0) error('no-roads', 'a level needs at least one road');

  // Widths come from a named set of three (`ROADS.WIDTHS`). Checked here as
  // well as in the placer, per the rule of the folder: a road an unnamed width
  // would still draw, still be crossable, and quietly stop matching the
  // hierarchy the town is supposed to read as.
  const allowed = new Set<number>(Object.values(ROADS.WIDTHS));
  for (const road of spec.roads) {
    if (road.width === undefined) continue;
    if (!allowed.has(road.width)) {
      error(
        'road-width-unknown',
        `the ${road.orient} way at ${road.pos} is ${road.width} wide, ` +
          `which is not one of the town's street widths`,
      );
    }
    if (!isStreet(road)) {
      error(
        'lane-with-width',
        `the cobbled way at ${road.pos} has a carriageway width, but a lane has no carriageway`,
      );
    }
  }

  if (!spec.roads.some(isStreet)) {
    // A town of nothing but footpaths has no carriageway to cross, so the
    // hazard the whole walk is built around simply isn't in it.
    error('no-streets', 'a level needs at least one carriageway, not only lanes');
  }

  // Half the ground a way takes up, measured from its centreline: a street
  // carries a footway either side, a lane is just its own width.
  const reach = (road: LevelSpec['roads'][number]): number =>
    isStreet(road) ? corridorFor(carriagewayWidth(road, ROADS.WIDTH)) / 2 : LANES.WIDTH / 2;

  for (const road of spec.roads) {
    const extent = road.orient === 'h' ? spec.world.height : spec.world.width;
    if (road.pos < reach(road) || road.pos > extent - reach(road)) {
      error(
        'road-off-map',
        `${road.orient} ${isStreet(road) ? 'road' : 'lane'} at ${road.pos} is too close ` +
          `to the edge for its footways`,
      );
    }
  }

  // No way may stop in open ground.
  //
  // Every end is either the map edge or a junction with something. A street
  // that runs on and stops in the middle of a block is tarmac ending in a blank
  // turning head — it reads as unfinished rather than organic, and when it is
  // one half of a jog it means the two halves never meet and the street network
  // has a break in it that the walker has to go round.
  //
  // Re-derived here rather than trusted, because `windStreets` decides one
  // orientation at a time and the second pass can move a crossing the first
  // pass ended on. That failure produced exactly this and nothing else caught it.
  for (const road of spec.roads) {
    const along = road.orient === 'h' ? spec.world.width : spec.world.height;
    const [from, to] = roadSpan(road, spec.world);
    for (const end of [from, to]) {
      if (end <= 0 || end >= along) continue;
      // Two conditions, and both are needed. The crossing way has to run past
      // this one's line — a street that stops short of it is not something you
      // can end at — and this end has to land within that way's own corridor,
      // which is where the two actually touch. A lane ends at the *footway*
      // edge of the street it comes out on rather than at its centreline, so
      // the tolerance is the crossing way's half-corridor, not half a
      // carriageway.
      const met = spec.roads.some(
        (o) =>
          o.orient !== road.orient &&
          spanReaches(o, road.pos) &&
          Math.abs(o.pos - end) <= reach(o) + 1,
      );
      if (!met) {
        error(
          'way-to-nowhere',
          `the ${road.orient} way at ${road.pos} ends at ${Math.round(end)}, ` +
            `where nothing crosses it`,
        );
      }
    }
  }

  for (let i = 0; i < spec.roads.length; i++) {
    for (let j = i + 1; j < spec.roads.length; j++) {
      const a = spec.roads[i];
      const b = spec.roads[j];
      if (a.orient !== b.orient) continue;
      // Two parallel ways only contend for ground where they run alongside each
      // other. Two collinear segments of one jogged street, at different
      // positions with spans that never overlap, are one street — not two roads
      // sharing a footway.
      if (!spansOverlap(a, b, spec.world)) continue;
      if (Math.abs(a.pos - b.pos) < reach(a) + reach(b)) {
        error(
          'roads-too-close',
          `${a.orient} ways at ${a.pos} and ${b.pos} share a footway`,
        );
      }
    }
  }
}

/** Whether two parallel ways run alongside each other for any of their length. */
function spansOverlap(
  a: LevelSpec['roads'][number],
  b: LevelSpec['roads'][number],
  world: { width: number; height: number },
): boolean {
  const [aFrom, aTo] = roadSpan(a, world);
  const [bFrom, bTo] = roadSpan(b, world);
  return aFrom < bTo && bFrom < aTo;
}

/** Plots: on the map, off the pavement, out of each other, and paintable. */
function checkBuildings(
  spec: LevelSpec,
  bands: readonly Rect[],
  world: Rect,
  error: Report,
  warn: Report,
): void {
  const where = (b: Rect): string => `(${Math.round(b.x)},${Math.round(b.y)})`;

  // A town with no houses in it is not a quiet level, it's a field with roads
  // across it — and the buildings are the level's only static obstacles, so
  // there would be nothing to walk the dog *around*.
  if (spec.buildings.length === 0) error('empty-town', 'the town has no buildings in it');

  for (const plot of spec.buildings) {
    if (!rectContains(world, plot)) error('building-off-map', `building ${where(plot)} leaves the map`);
    if (plot.w < BUILDINGS.BAY_MIN_WIDTH || plot.h < BUILDINGS.BAY_MIN_WIDTH) {
      error('building-too-small', `building ${where(plot)} is too small to hold a cottage`);
    }
    if (plot.tall - BUILDINGS.BAY_STEP < BUILDINGS.SHOPFRONT_HEIGHT + BUILDINGS.WINDOW_H) {
      error('building-too-short', `building ${where(plot)} is too short for its upper windows`);
    }

    for (const band of bands) {
      if (rectsOverlap(plot, band)) {
        error('building-on-footway', `building ${where(plot)} stands on a footway`);
      }
    }

    const front = frontFacing(plot, bands);

    // The front door has to open onto the pavement.
    //
    // Re-derived here rather than trusted, per the rule of the folder: the
    // placer already lays every plot flush to the block edge it fronts, and a
    // heuristic that quietly stops working produces a level that still
    // generates and is simply wrong — a house standing in the middle of a
    // block with its door opening onto a weed patch. This is the check that
    // turns "the placer tries to" into "the level does".
    //
    // A patio house is the one legitimate gap: its forecourt is taken out of
    // the building's own depth, so the wall stands back by exactly
    // `PATIO_DEPTH` with its own paving filling the space. Nothing else may.
    const reach = plot.patio ? BUILDINGS.PATIO_DEPTH : 0;
    if (faceGaps(plot, bands)[front] > reach + DOORSTEP_SLACK) {
      error(
        'door-off-pavement',
        `building ${where(plot)} fronts ${front} onto no pavement, so its door ` +
          `opens onto open ground`,
      );
    }

    const alongX = front === 'north' || front === 'south';
    const bays = splitFrontage(
      plot,
      alongX,
      bayMinLength(
        alongX ? plot.h : plot.w,
        alongX,
        BUILDINGS.BAY_MIN_WIDTH,
        BUILDINGS.BAY_MIN_RATIO,
      ),
      BUILDINGS.BAY_MAX,
      BUILDINGS.BAY_JITTER,
    );
    if (plot.tones.length < bays.length) {
      error(
        'not-enough-tones',
        `building ${where(plot)} has ${bays.length} cottages but only ${plot.tones.length} tones, ` +
          `so two neighbours would share a colour`,
      );
    }

    // The ridge rule, on the cottages the renderer will actually draw.
    //
    // `gableSlope` runs a roof's ridge along the cottage's longer side, and the
    // camera looks north and down at 58° — so a cottage wider than it is deep
    // turns a whole roof slope square at the camera, which renders as one
    // enormous featureless quad. The placer sizes every plot to clear this, but
    // a plot is trimmed and a patio is taken out of its depth *after* that, and
    // this is the check that turns "the placer tries to" into "the level does".
    //
    // Held to the proportion rather than the bare inequality, because the bare
    // inequality passes a cottage 78 wide and 79 deep: a square house whose
    // ridge runs the right way and reads as running no way at all.
    for (const bay of bays) {
      if (bay.h < bay.w * BUILDINGS.BAY_MIN_RATIO) {
        error(
          'ridge-across-camera',
          `cottage ${where(bay)} of building ${where(plot)} is ${Math.round(bay.w)} wide and ` +
            `${Math.round(bay.h)} deep, so its roof turns a slope to the camera`,
        );
        break;
      }
    }

    if (!plot.patio) continue;
    // A patio on a north-facing front is drawn underneath the house's own roof
    // at this camera tilt and may as well not exist — see the two patio rules
    // above `BUILDINGS.LIST`.
    if (front !== 'south') {
      error('patio-hidden', `building ${where(plot)} faces ${front}, so its patio can't be seen`);
    }
    const forecourt = patioRect(plot, front, BUILDINGS.PATIO_DEPTH);
    for (const band of bands) {
      if (rectsOverlap(forecourt, band)) {
        error('patio-on-footway', `the patio of building ${where(plot)} spills onto a footway`);
      }
    }
  }

  for (let i = 0; i < spec.buildings.length; i++) {
    for (let j = i + 1; j < spec.buildings.length; j++) {
      if (rectsOverlap(spec.buildings[i], spec.buildings[j])) {
        error(
          'buildings-overlap',
          `buildings ${where(spec.buildings[i])} and ${where(spec.buildings[j])} intersect`,
        );
      }
    }
  }

  // The brick coursing is the main thing telling two similarly coloured
  // cottages apart, so a town of one material loses half its variety.
  const tones = spec.buildings.flatMap((b) => b.tones);
  if (tones.length > 1) {
    if (!tones.some((t) => t.material === 'brick')) warn('no-brick', 'no brick walls in the town');
    if (!tones.some((t) => t.material === 'render')) {
      warn('no-render', 'no rendered walls in the town');
    }
  }
}

/** Parks: grass never on the pavement, and never under something else. */
function checkParks(spec: LevelSpec, bands: readonly Rect[], error: Report): void {
  for (const park of spec.parks) {
    // Drawn with a kerb around them, so it's the outer edge that has to clear.
    const withKerb = inflateRect(park, 2);
    for (const band of bands) {
      if (rectsOverlap(withKerb, band)) {
        error('park-on-footway', `park (${Math.round(park.x)},${Math.round(park.y)}) reaches a footway`);
      }
    }
    for (const plot of spec.buildings) {
      if (rectsOverlap(park, plot)) {
        error('park-in-building', `park (${Math.round(park.x)},${Math.round(park.y)}) runs under a house`);
      }
    }
    // Two textured slabs on one patch z-fight, and the home pad is drawn above
    // the ground for the whole run whether or not it's visible yet.
    if (rectsOverlap(park, spec.home)) {
      error('park-under-home', `park (${Math.round(park.x)},${Math.round(park.y)}) is under the home pad`);
    }
  }
}

/**
 * Trees, which are the easiest thing in a level to get wrong: the trunks are
 * solid and the canopies are drawn well north of them, so a tree in the wrong
 * place either blocks a route or hides the walker.
 */
function checkTrees(
  spec: LevelSpec,
  bands: readonly Rect[],
  roads: readonly Rect[],
  world: Rect,
  error: Report,
  warn: Report,
): void {
  const where = (t: { x: number; y: number }): string =>
    `(${Math.round(t.x)},${Math.round(t.y)})`;

  for (const tree of spec.trees) {
    const envelope = treeEnvelope(tree);
    const ground = canopyRect(tree.x, tree.y, envelope);
    const drawn = apparentCanopyRect(
      tree.x,
      tree.y,
      envelope,
      treeTrunkTop(tree),
      tree.kind,
      CAMERA.TILT_DEG,
    );

    if (!rectContains(world, ground)) error('tree-off-map', `tree ${where(tree)} leaves the map`);
    if (roads.some((r) => rectsOverlap(ground, r))) {
      error('tree-over-road', `tree ${where(tree)} overhangs the carriageway`);
    }
    if (spec.buildings.some((b) => rectsOverlap(ground, b))) {
      error('tree-in-building', `tree ${where(tree)} stands in a house`);
    }
    for (const band of bands) {
      if (rectsOverlap(ground, band)) {
        error('tree-over-footway', `tree ${where(tree)} overhangs a footway`);
      }
      // The strict version: a trunk can sit clear of the pavement while its
      // foliage is drawn on top of it, which is what actually hides the walker.
      if (rectsOverlap(drawn, inflateRect(band, FOOTWAY_MARGIN))) {
        error(
          'tree-renders-over-footway',
          `tree ${where(tree)} is drawn over (or within ${FOOTWAY_MARGIN} of) a footway`,
        );
      }
    }

    for (const bin of spec.bins) {
      if (
        Math.hypot(tree.x - bin.x, tree.y - bin.y) <=
        OBJECTIVE.BIN_RADIUS + TREES.TRUNK_RADIUS
      ) {
        error('tree-crowds-bin', `tree ${where(tree)} stands in the drop zone of the bin ${at(bin)}`);
      }
    }
    if (rectsOverlap(ground, spec.home)) {
      error('tree-in-home', `tree ${where(tree)} is inside the home zone`);
    }

    const trunk = TREES.TRUNK_RADIUS * tree.scale;
    const dogSpawn = { x: spec.start.x + DOG_OFFSET, y: spec.start.y };
    if (Math.hypot(tree.x - spec.start.x, tree.y - spec.start.y) <= PLAYER.RADIUS + trunk) {
      error('tree-on-spawn', `tree ${where(tree)} is on the walker's start`);
    }
    if (Math.hypot(tree.x - dogSpawn.x, tree.y - dogSpawn.y) <= DOG.RADIUS + trunk) {
      error('tree-on-spawn', `tree ${where(tree)} is on the dog's start`);
    }
  }

  for (let i = 0; i < spec.trees.length; i++) {
    for (let j = i + 1; j < spec.trees.length; j++) {
      const a = spec.trees[i];
      const b = spec.trees[j];
      const gap = Math.hypot(a.x - b.x, a.y - b.y);
      if (gap <= TREES.TRUNK_RADIUS * (a.scale + b.scale)) {
        error('trees-overlap', `trees ${where(a)} and ${where(b)} share a trunk`);
      }
      // Two identical trees side by side is exactly what reads as copy-paste.
      if (gap < 120 && a.kind === b.kind && a.scale === b.scale) {
        warn('identical-neighbours', `trees ${where(a)} and ${where(b)} are identical neighbours`);
      }
    }
  }

  if (spec.parks.length > 0) {
    const inPark = spec.trees.filter((t) =>
      spec.parks.some((p) => t.x >= p.x && t.x <= p.x + p.w && t.y >= p.y && t.y <= p.y + p.h),
    );
    if (inPark.length === 0) warn('bare-parks', 'no trees growing on any of the grass');
  }
}

/** Grass: there has to be some, and it has to be usable. */
function checkGrass(spec: LevelSpec, error: Report): void {
  // The rule that makes this a completability check rather than a look one: a
  // dog will only do its business on grass (see `systems/grass.ts`), so a level
  // with none is a level whose first objective can never be reached.
  if (spec.parks.length === 0) {
    error('no-grass', 'the level has no grass, so the dog can never do its business');
    return;
  }

  // …and the grass has to be big enough to get a whole dog onto. `onGrass`
  // insets by `GRASS_MARGIN` on every side, so a patch thinner than twice that
  // is a park the dog can stand beside but never on.
  const usable = spec.parks.filter(
    (p) => p.w > GRASS_MARGIN * 2 && p.h > GRASS_MARGIN * 2,
  );
  if (usable.length === 0) {
    error('grass-too-small', 'every patch of grass is too small for the dog to stand on');
  }
}

/** The bin, the home pad and the two spawn points: on open ground, and apart. */
function checkLandmarks(
  spec: LevelSpec,
  bands: readonly Rect[],
  world: Rect,
  error: Report,
): void {
  const dogSpawn = { x: spec.start.x + DOG_OFFSET, y: spec.start.y };
  // The walker's own house counts as a building here. It is not in
  // `spec.buildings` — the town may not build on the home plot, so it isn't a
  // town plot — but it is drawn and it is solid, and a start point inside it is
  // the same bug whichever list the wall came from.
  const solids = [...spec.buildings, homeHouseRect(spec.home)];
  const solid = (p: { x: number; y: number }, r: number): boolean =>
    solids.some((b) => rectsOverlap(inflateRect(b, r), { x: p.x, y: p.y, w: 0, h: 0 }));

  for (const bin of spec.bins) {
    if (solid(bin, OBJECTIVE.BIN.SIZE / 2)) {
      error('bin-in-building', `the bin ${at(bin)} is inside a house`);
    }
  }
  if (solid(spec.start, PLAYER.RADIUS)) error('start-in-building', 'the walker starts in a house');
  if (solid(dogSpawn, DOG.RADIUS)) error('dog-in-building', 'the dog starts in a house');

  if (!rectContains(world, spec.home)) error('home-off-map', 'the home zone leaves the map');
  if (spec.buildings.some((b) => rectsOverlap(b, spec.home))) {
    error('home-in-building', 'the home zone overlaps a house');
  }
  for (const bin of spec.bins) {
    const body = inflateRect({ ...bin, w: 0, h: 0 }, OBJECTIVE.BIN.SIZE / 2);
    // The bin's drop zone is wider than a footway, so a bin on the pavement
    // pinches the route past it.
    if (bands.some((b) => rectsOverlap(b, body))) {
      error('bin-on-footway', `the bin ${at(bin)} stands on a footway`);
    }
    // …and a bin nowhere near one is street furniture with no street. The
    // player is sent to find these, and an object standing in the middle of a
    // block's back gardens can only be found by walking over it — so a bin has
    // to stand **against a frontage**, which is where a town actually bolts one
    // down. `landmarks.ts` → `placeBins` places them that way; this refuses to
    // believe it.
    const kerbside = bands.some((b) => rectGap(b, pointRect(bin.x, bin.y)) <= BIN_KERB_REACH);
    if (!kerbside) {
      error('bin-in-the-open', `the bin ${at(bin)} stands in open ground, away from any pavement`);
    }
    // And **not by the grass**. This was a licence rather than an offence until
    // the cleanup was merged into the squat: a bin was allowed beside a green,
    // and `placeBins` preferred it there, because the green is where the bag
    // gets filled. That is the reason it is wrong. The bag is filled at the most
    // predictable moment of the walk, and a bin already in shot when it happens
    // is the back half of the errand deleted — the player never has to look for
    // one. See `landmarks.ts` → `binGreenClearance`.
    const toGreen = Math.min(
      ...spec.parks.map((p) => rectGap(p, pointRect(bin.x, bin.y))),
      Infinity,
    );
    if (toGreen < binGreenClearance(spec.bounds)) {
      error(
        'bin-beside-the-grass',
        `the bin ${at(bin)} is only ${Math.round(toGreen)} from a green — the bag would be ` +
          'binned where the dog went',
      );
    }
  }
  for (let i = 0; i < spec.bins.length; i++) {
    for (let j = i + 1; j < spec.bins.length; j++) {
      const gap = Math.hypot(spec.bins[i].x - spec.bins[j].x, spec.bins[i].y - spec.bins[j].y);
      // Two bins within a drop zone of each other are one bin drawn twice —
      // and the second one's ring would be drawn inside the first one's.
      if (gap < OBJECTIVE.BIN_RADIUS * 2) {
        error('bins-crowded', `the bins ${at(spec.bins[i])} and ${at(spec.bins[j])} are on top of each other`);
      }
    }
  }

  // A walk is a round trip from the front door, so the start *is* home —
  // specifically the doormat of the house on the home plot. Letting the two
  // drift apart would mean the level opens somewhere the player is never sent
  // back to, and the win zone would be a place they had simply left.
  const home = { x: spec.home.x + spec.home.w / 2, y: spec.home.y + spec.home.h / 2 };
  const doorstep = homeDoorstep(spec.home);
  if (Math.hypot(doorstep.x - spec.start.x, doorstep.y - spec.start.y) > 1e-6) {
    error('start-away-from-home', 'the walker does not start on their own doorstep');
  }

  for (const bin of spec.bins) {
    const gap = Math.hypot(home.x - bin.x, home.y - bin.y);
    if (gap < LANDMARK_SEPARATION) {
      error(
        'landmarks-crowded',
        `home and the bin ${at(bin)} are only ${Math.round(gap)} apart — there's no walk in it`,
      );
    }
  }
  // The grass, held to the same standard as the bin and for a stronger reason:
  // getting the dog somewhere it will go is the *first* half of the walk, and a
  // green over the garden fence deletes it. This used to allow anything past
  // half the landmark separation — 85 units, about the width of the home plot —
  // so a park could and routinely did stand against the front gate.
  //
  // Measured edge to edge, not centre to centre: a big green whose middle is a
  // street away can still have its near corner over the garden fence.
  const toGrass = Math.min(...spec.parks.map((p) => rectGap(p, spec.home)));
  if (spec.parks.length > 0 && toGrass < greenSeparation(spec.bounds)) {
    error(
      'grass-on-the-doorstep',
      `the nearest grass is ${Math.round(toGrass)} from the front door — close enough to be part of the garden`,
    );
  }
}

/**
 * **The completability proof.**
 *
 * One flood fill from the walker's start over ground grown by their own radius,
 * then check that both objectives fall inside it. One fill rather than two
 * because reachability on a static map is symmetric and transitive: if the bin
 * and home are both in the start's connected component then the whole errand —
 * start → bin → home — is walkable, in that order or any other.
 *
 * Traffic is not part of this fill. It moves, so it can't block a route; what
 * it can do is make a crossing unsurvivable, and that is checked separately
 * against the crossing window in `checkTraffic`.
 */
function checkCompletable(spec: LevelSpec, error: Report): void {
  const grid = buildWalkGrid(
    // The walk, not the map. Filling the surround would prove things about
    // ground the walker is clamped out of, at several times the cost — and the
    // boundary is what makes the fill honest, because a route that leaves the
    // playable rectangle and comes back is not a route.
    spec.bounds,
    // The walker's own house is solid too, so the fill has to know about it:
    // it stands on the back of the home plot, and the plot is the win zone. A
    // fill that walked through it could call home reachable when the only way
    // in was the front garden the house had sealed off.
    [...spec.buildings, homeHouseRect(spec.home)],
    spec.trees.map((t) => ({ x: t.x, y: t.y, r: TREES.TRUNK_RADIUS * t.scale })),
    PLAYER.RADIUS,
  );

  if (!isOpen(grid, spec.start.x, spec.start.y)) {
    error('start-blocked', 'the walker starts on ground they cannot stand on');
    return;
  }

  const reached = floodFrom(grid, spec.start);
  // **Every** bin, not just the nearest. A bin the walker cannot get to is one
  // they can see, walk towards, and never use — and since the marker rings show
  // all of them, an unreachable one is an instruction to walk into a wall.
  for (const bin of spec.bins) {
    if (!reachedNear(grid, reached, bin, OBJECTIVE.BIN_RADIUS)) {
      error('bin-unreachable', `the walker cannot reach the bin ${at(bin)} from where they start`);
    }
  }
  // Grass is on the critical path now, so it needs the same proof as the bin:
  // an unreachable park is an unfinishable level, however pretty it looks.
  // Shrunk by `GRASS_MARGIN` because that is how much of it actually counts.
  if (!spec.parks.some((p) => reachedInside(grid, reached, inflateRect(p, -GRASS_MARGIN)))) {
    error('grass-unreachable', 'the walker cannot get the dog to any grass');
  }
  // Shrunk by the walker's radius: they have to fit *inside* the trigger zone,
  // not merely clip its corner.
  if (!reachedInside(grid, reached, inflateRect(spec.home, -PLAYER.RADIUS))) {
    error('home-unreachable', 'the walker cannot reach the home zone from where they start');
  }
}

/**
 * Traffic. The schedule has to be well-formed, and — the rule that matters —
 * every road has to leave a gap long enough to walk across it in.
 */
function checkTraffic(spec: LevelSpec, error: Report, warn: Report): void {
  const { schedule, loopMs, maxVehicles, speed } = spec.traffic;
  if (maxVehicles < 1) error('no-traffic-budget', 'maxVehicles must be at least 1');
  if (loopMs <= 0) error('bad-traffic-loop', 'the traffic schedule has no length');
  if (speed <= 0) error('bad-traffic-speed', 'vehicles must move');

  for (let i = 1; i < schedule.length; i++) {
    if (schedule[i].atMs < schedule[i - 1].atMs) {
      error('traffic-unsorted', 'the traffic schedule is not in time order');
      break;
    }
  }
  for (const release of schedule) {
    if (release.road < 0 || release.road >= spec.roads.length) {
      error('traffic-off-road', `a vehicle is released onto road ${release.road}, which doesn't exist`);
      continue;
    }
    if (release.atMs >= loopMs) {
      error('traffic-past-loop', `a vehicle is released at ${release.atMs}ms, after the loop ends`);
    }

    // Re-derived rather than trusted, like every other rule here. A car on a
    // cobbled footpath ends the run with something the player could not have
    // seen coming, and a car on a street that stops mid-town appears and
    // vanishes in mid-air. `planTraffic` already refuses both; this is what
    // makes it true rather than intended.
    const road = spec.roads[release.road];
    if (!isStreet(road)) {
      error('traffic-on-lane', `a vehicle is released onto the pedestrian lane at ${road.pos}`);
    } else if (!spansMap(road, spec.world)) {
      error(
        'traffic-on-stub',
        `a vehicle is released onto the ${road.orient} street at ${road.pos}, ` +
          `which does not run across the map`,
      );
    }
  }

  for (let road = 0; road < spec.roads.length; road++) {
    const times = schedule.filter((r) => r.road === road).map((r) => r.atMs);
    if (times.length === 0) continue;
    // Measured against *this* street's width. A high street is further to get
    // across, so it has to leave a longer gap than the back lane beside it —
    // which is the whole reason a wider street is a difficulty decision rather
    // than a drawing one.
    const floor = minRoadGapMs(speed, carriagewayWidth(spec.roads[road], ROADS.WIDTH));
    // The wrap counts: a schedule that leaves a comfortable gap everywhere
    // except where it loops has a burst in it, once a minute, forever.
    const gaps = times.slice(1).map((t, i) => t - times[i]);
    gaps.push(loopMs - times[times.length - 1] + times[0]);
    const tightest = Math.min(...gaps);
    if (tightest < floor) {
      error(
        'crossing-impossible',
        `road ${road} leaves only ${Math.round(tightest)}ms between vehicles, ` +
          `but crossing it takes up to ${Math.round(floor)}ms`,
      );
    }
  }

  if (schedule.length > 0 && schedule[0].atMs < 1500) {
    warn('traffic-opens-hot', 'the first vehicle arrives before the player has got going');
  }
}

/** Critters: on the map, out of the houses, and not in the walker's lap. */
function checkHazards(spec: LevelSpec, world: Rect, error: Report, warn: Report): void {
  for (const spawn of spec.hazards.spawns) {
    if (spawn.atMs < 0) error('critter-early', `a ${spawn.type} has a negative spawn time`);
    // An unplaced critter is scattered by `HazardManager` at construction, which
    // avoids the buildings itself — there is nothing here to check.
    if (spawn.x === undefined || spawn.y === undefined) continue;

    const where = `(${Math.round(spawn.x)},${Math.round(spawn.y)})`;
    const point = { x: spawn.x, y: spawn.y, w: 0, h: 0 };
    if (spawn.x < 0 || spawn.y < 0 || spawn.x > world.w || spawn.y > world.h) {
      error('critter-off-map', `${spawn.type} at ${where} starts off the map`);
    }
    if (spec.buildings.some((b) => rectsOverlap(inflateRect(b, HAZARD.SIZE / 2), point))) {
      error('critter-in-building', `${spawn.type} at ${where} starts inside a house`);
    }
    if (
      spawn.atMs === 0 &&
      Math.hypot(spawn.x - spec.start.x, spawn.y - spec.start.y) < HAZARD.HYPER_PULL_RADIUS
    ) {
      warn('critter-on-start', `${spawn.type} at ${where} opens the run with the dog already pulling`);
    }
  }

  if (spec.hazards.speedMin > spec.hazards.speedMax) {
    error('critter-speed-band', 'the critter speed band is inverted');
  }
}
